import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import { ClipboardList } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MecordColors } from "Constants/Color";
import { getWarehouseByUser } from "slices/Masters/warehosue/thunk";
import Instock from "./Instock/instock";
import WarehouseDailyLog from "./DailyLog/DailyLog";
import { BoxSeam, ExclamationTriangle } from "react-bootstrap-icons";
import { clearAllTableStates } from "utils/clearTableStates";
import NoWarehousesMessage from "components/NoWarehouse/NoWarehouseMessage";

const WarehouseStockOverview = () => {
  const dispatch = useDispatch<any>();
  const [warehouseId, setWarehouseId] = useState<any>(() => {
    return sessionStorage.getItem("selectedWarehouseId");
  });

  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("WarehouseStockOverviewTab")) || 1,
  );

  const { warehouseByUserList, warehouseByUserLoading } = useSelector(
    (state: any) => state.warehouse,
  );

  useEffect(() => {
    if (!warehouseByUserList?.length) {
      setWarehouseId(null);
      return;
    }

    const storedWarehouseId = sessionStorage.getItem("selectedWarehouseId");

    if (storedWarehouseId) {
      setWarehouseId(storedWarehouseId);
      return;
    }

    setWarehouseId(warehouseByUserList[0].warehouse_id);
  }, [warehouseByUserList]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const newTab = newValue + 1;
      setTab(newTab);
      clearAllTableStates();
      sessionStorage.setItem("WarehouseStockOverviewTab", newTab.toString());
    },
    [],
  );

  const statuses = useMemo(
    () => [
      {
        label: "Total Sales Orders",
        icon: <BoxSeam size={16} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: 120,
      },
      {
        label: "Total Quantity",
        icon: <ClipboardList size={16} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: 35,
      },
      {
        label: "Robust Stock",
        icon: <BoxSeam size={16} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: 50,
      },
      {
        label: "Damaged Stock",
        icon: <ExclamationTriangle size={16} />,
        bgColor: "bg-red-50",
        textColor: "text-red-600",
        count: 82,
      },
    ],
    [],
  );

  useEffect(() => {
    dispatch(getWarehouseByUser());
  }, [dispatch]);

  return (
    <div
      className="page-content px-3 mecord-kpi-list-page"
      style={{ "--mecord-table-offset": "400px" } as React.CSSProperties}
    >
      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="mecord-list-panel"
        style={{
          ...defaultInnerStyle,
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>Warehouse</h6>
            <Autocomplete
              options={warehouseByUserList ?? []}
              loading={warehouseByUserLoading}
              getOptionLabel={(option: any) => option?.warehouse_name || ""}
              value={
                warehouseByUserList.find(
                  (opt: any) => opt.warehouse_id === warehouseId,
                ) ?? null
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.warehouse_id === value.warehouse_id
              }
              onChange={(_, selected) => {
                setWarehouseId(selected?.warehouse_id ?? null);
                sessionStorage.setItem(
                  "selectedWarehouseId",
                  selected.warehouse_id ?? "",
                );
                clearAllTableStates();
              }}
              disableClearable
              sx={{ ...CustomStyle?.singleselect, width: 280 }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) =>
                    typeof opt === "string"
                      ? opt
                      : String(opt.warehouse_name),
                )
              }
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="warehouse"
                  placeholder="Select Warehouse"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      height: 34,
                    },
                  }}
                />
              )}
            />
          </div>

          <div className="mecord-tabs-wrap">
            <Box sx={{ width: "100%" }}>
              <Tabs
                value={tab - 1}
                onChange={handleTabChange}
                sx={{
                  minHeight: 30,
                  "& .MuiTab-root": {
                    fontFamily: "'Poppins', sans-serif",
                    textTransform: "none",
                    color: "#555555",
                    minHeight: 30,
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    transition: "color 0.2s ease",
                  },
                  "& .MuiTab-root:hover": {
                    color: "#d2363a",
                  },
                  "& .Mui-selected": {
                    color: "#d2363a !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#d2363a",
                    height: "3px",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <Tab label="Daily Log" />
                <Tab label="In Stock" />
              </Tabs>
              <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
            </Box>
          </div>
        </div>

        <div
          className="d-flex flex-column"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {warehouseByUserList.length === 0 ? (
            <NoWarehousesMessage />
          ) : tab === 1 ? (
            <WarehouseDailyLog
              key={`daily-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : tab === 2 ? (
            <Instock
              key={`instock-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WarehouseStockOverview;
